import { computed, effect, inject, Injectable, InjectionToken, signal, untracked } from '@angular/core';
import { CheckBoxState } from '@progress/kendo-angular-inputs';
import { UnityFacet } from '@unity/clients';
import { FilterState, FILTERSTATE_SWITCHES_KEY, SWITCHES_KEY } from '@unity/utils/filter-state';
import { SwitchStateEvent } from './gridify.service';

export const FILTERSTATE_KEY = new InjectionToken<string>('FILTERSTATE_KEY');
type _SWITCHES = FILTERSTATE_SWITCHES_KEY;

@Injectable()
export class FilterStateService<T> {
  private readonly key = inject(FILTERSTATE_KEY);
  private readonly switchSectionKey = SWITCHES_KEY;

  /* Data */

  private _filterCache: Record<keyof T | _SWITCHES, Record<string, boolean>>;

  /* Signals */

  private readonly $_filterStates = signal(new Map<keyof T | _SWITCHES, FilterState<T>>());

  public readonly $states = computed(() => {
    return [...this.$_filterStates().values()];
  });
  public readonly $valueStates = computed(() => this.$states().filter(x => x.key !== this.switchSectionKey));
  public readonly $switchStates = computed<SwitchStateEvent<T>[] | undefined>(() => {
    const section = this.$_filterStates().get(this.switchSectionKey);
    if (!section) {
      return;
    }
    return section.items.map(
      x =>
        ({
          item: x,
          state: section.get(x as string),
        }) as SwitchStateEvent<T>
    );
  });

  /* Constructor */

  public constructor() {
    this._filterCache = this.loadPreferences();
    effect(() => {
      const states = this.$_filterStates();
      this.updatePreferences(states);
    });
  }

  /* Methods */

  private get storageKey() {
    return `_filter_${this.key}`;
  }

  private loadPreferences() {
    const preferences = sessionStorage.getItem(this.storageKey);
    if (preferences === null) {
      return {};
    }
    return JSON.parse(preferences);
  }

  private loadSavedSection(sectionKey: keyof T | _SWITCHES) {
    const state: Record<string, boolean> = this._filterCache[sectionKey] ?? {};
    return state;
  }

  private saveSection(filterSection: keyof T | _SWITCHES, state: Record<string, boolean>) {
    this._filterCache[filterSection] = state;
  }

  private updateState(states: Map<keyof T | _SWITCHES, FilterState<T>>) {
    this.$_filterStates.set(new Map(states));
  }

  private updatePreferences(states: Map<keyof T | _SWITCHES, FilterState<T>>) {
    for (const [key, value] of states) {
      this.saveSection(key, value.getMapSnapshot());
    }
    const preferences = JSON.stringify(this._filterCache);
    sessionStorage.setItem(this.storageKey, preferences);
  }

  private buildFilter(facet: UnityFacet) {
    const key = facet.property as keyof T;
    const fil = new FilterState<T>(key, facet.display_name, facet.values);
    const saved = this.loadSavedSection(key);
    if (saved && Object.values(saved).length > 0) {
      fil.setMapSnapshot(saved);
    }
    return fil;
  }

  private buildSwitchBlock(switchStates: SwitchStateEvent<T>[]) {
    const switchFilter = new FilterState<T>(
      this.switchSectionKey,
      this.switchSectionKey,
      switchStates.map(x => x.item)
    );
    const saved = this.loadSavedSection(this.switchSectionKey);
    if (saved && Object.values(saved).length > 0) {
      switchFilter.setMapSnapshot(saved);
    } else {
      switchStates.forEach(s => switchFilter.toggle(s.item as string, s.state));
    }
    return switchFilter;
  }

  public buildFilters(facets: UnityFacet[], switchStates: SwitchStateEvent<T>[]) {
    const filters = facets.map(cfg => this.buildFilter(cfg));
    const switchFilter = this.buildSwitchBlock(switchStates);

    const states = new Map();
    filters.forEach(f => states.set(f.key, f));
    states.set(this.switchSectionKey, switchFilter);
    this.updateState(states);
  }

  public toggle(key: keyof T, item: string, state?: boolean) {
    const states = untracked(this.$_filterStates);
    states.get(key)?.toggle(item, state);
    this.updateState(states);
  }

  public toggleAll(key: keyof T, state: CheckBoxState) {
    const states = untracked(this.$_filterStates);
    states.get(key)?.toggleAll(state);
    this.updateState(states);
  }

  public toggleSwitch(event: SwitchStateEvent<T>) {
    this.toggle(this.switchSectionKey as keyof T, event.item as string, event.state);
  }
}
